import { useState, useEffect } from "react";
import { Calculator } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtEUR } from "@/lib/format";
import { FieldText, FieldSelect } from "./champs";

export function BlocImmatriculation({ adm, onPatch, canEdit, dossierId }) {
  const [taxeCalcul, setTaxeCalcul] = useState(null);
  const [tCalc, setTCalc] = useState({ puissance_fiscale: 7, energie: "essence", co2_g_km: 130 });
  const immat = adm.immatriculation || {};
  const [form, setForm] = useState(immat);
  useEffect(() => { setForm(immat); /* eslint-disable-next-line */ }, [JSON.stringify(immat)]);

  const calc = async () => {
    try {
      const r = await api.post(`/dossiers/${dossierId}/calculer-taxes`, tCalc);
      setTaxeCalcul(r.data);
      onPatch({ taxe_regionale: r.data.taxe_regionale, malus_co2: r.data.malus_co2,
        malus_masse: r.data.malus_masse, redevance_ants: r.data.redevance_ants,
        taxe_gestion: r.data.taxe_gestion, total_taxes: r.data.total });
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="bloc-immat">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12.5px]">
        <FieldSelect label="Statut" value={form.statut || "a_faire"} disabled={!canEdit}
          onChange={(v) => { setForm({ ...form, statut: v }); onPatch({ statut: v }); }}
          options={[["a_faire", "À faire"], ["demande_deposee", "Demande déposée"],
            ["cpi_obtenu", "CPI obtenu"], ["titre_recu", "Titre reçu"],
            ["remis_client", "Remis au client"], ["non_applicable", "Non applicable"]]}
          testId="immat-statut" />
        <FieldText label="Immat. définitive" value={form.immat_definitive || ""} disabled={!canEdit}
          onBlur={(v) => onPatch({ immat_definitive: v })}
          onChange={(v) => setForm({ ...form, immat_definitive: v })} testId="immat-definitive" />
        <FieldText label="Date dépôt demande" type="date" value={(form.date_depot_demande || "").slice(0, 10)} disabled={!canEdit}
          onBlur={(v) => onPatch({ date_depot_demande: v })}
          onChange={(v) => setForm({ ...form, date_depot_demande: v })} testId="immat-date-depot" />
        <FieldText label="Date réception titre" type="date" value={(form.date_reception_titre || "").slice(0, 10)} disabled={!canEdit}
          onBlur={(v) => onPatch({ date_reception_titre: v })}
          onChange={(v) => setForm({ ...form, date_reception_titre: v })} testId="immat-date-titre" />
      </div>

      {canEdit && (
        <div className="mt-4 p-3 bg-[#F1F4F9] rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="w-4 h-4 text-[#0369A1]" />
            <span className="text-[12.5px] font-semibold">Calcul des taxes carte grise</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <FieldText label="CV fiscaux" type="number" value={tCalc.puissance_fiscale}
              onChange={(v) => setTCalc({ ...tCalc, puissance_fiscale: parseInt(v) || 0 })} testId="calc-cv" />
            <FieldSelect label="Énergie" value={tCalc.energie}
              onChange={(v) => setTCalc({ ...tCalc, energie: v })}
              options={[["essence", "Essence"], ["gazole", "Gazole"], ["hybride", "Hybride"],
                ["electrique", "Électrique"], ["gpl", "GPL"]]} testId="calc-energie" />
            <FieldText label="CO2 g/km" type="number" value={tCalc.co2_g_km || ""}
              onChange={(v) => setTCalc({ ...tCalc, co2_g_km: parseInt(v) || 0 })} testId="calc-co2" />
          </div>
          <div className="flex items-center justify-between">
            <button onClick={calc}
              className="px-3 py-1.5 bg-[#0369A1] text-white text-[12px] font-semibold rounded-full hover:bg-[#075985]"
              data-testid="calc-taxes-btn">Calculer</button>
            {taxeCalcul && (
              <div className="text-[12px]">
                <b>Total : {fmtEUR(taxeCalcul.total)}</b>
              </div>
            )}
          </div>
        </div>
      )}
      {immat.total_taxes != null && (
        <div className="mt-3 text-[12.5px] font-semibold">Taxes enregistrées : <b>{fmtEUR(immat.total_taxes)}</b></div>
      )}
    </div>
  );
}

export function BlocFinancement({ adm, onPatch, canEdit }) {
  const fin = adm.financement || {};
  const [form, setForm] = useState(fin);
  useEffect(() => { setForm(fin); /* eslint-disable-next-line */ }, [JSON.stringify(fin)]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12.5px]" data-testid="bloc-financement">
      <FieldSelect label="Mode" value={form.mode || "comptant"} disabled={!canEdit}
        onChange={(v) => { setForm({ ...form, mode: v }); onPatch({ mode: v }); }}
        options={[["comptant", "Comptant"], ["credit", "Crédit classique"],
          ["loa", "LOA"], ["lld", "LLD"], ["mixte", "Mixte"]]} testId="fin-mode" />
      <FieldSelect label="Statut" value={form.statut || "a_monter"} disabled={!canEdit}
        onChange={(v) => { setForm({ ...form, statut: v }); onPatch({ statut: v }); }}
        options={[["a_monter", "À monter"], ["transmis", "Transmis au financeur"],
          ["accord", "Accord de principe"], ["fonds_debloques", "Fonds débloqués"],
          ["refuse", "Refusé"], ["non_applicable", "Non applicable"]]} testId="fin-statut" />
      <FieldText label="Financeur" value={form.financeur || ""} disabled={!canEdit}
        onBlur={(v) => onPatch({ financeur: v })}
        onChange={(v) => setForm({ ...form, financeur: v })} testId="fin-financeur" />
      <FieldText label="Montant financé (€)" type="number" value={form.montant || ""} disabled={!canEdit}
        onBlur={(v) => onPatch({ montant: parseFloat(v) || 0 })}
        onChange={(v) => setForm({ ...form, montant: v })} testId="fin-montant" />
    </div>
  );
}

export function BlocConditionsLivraison({ adm, onPatch, canEdit }) {
  const liv = adm.livraison || {};
  const conds = liv.conditions || {};
  return (
    <div data-testid="bloc-conditions-livraison">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12.5px]">
        <FieldText label="Date prévue" type="date" value={(liv.date_prevue || "").slice(0, 10)} disabled={!canEdit}
          onBlur={(v) => onPatch({ date_prevue: v })} onChange={() => {}} testId="liv-date-prevue" />
        <FieldText label="Lieu" value={liv.lieu || ""} disabled={!canEdit}
          onBlur={(v) => onPatch({ lieu: v })} onChange={() => {}} testId="liv-lieu" />
      </div>
      <div className="mt-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-[--idc-muted] mb-1.5">Conditions de libération</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {[["vehicule_disponible", "Véhicule disponible"], ["preparation_terminee", "Préparation terminée"],
            ["immatriculation_ok", "Immatriculation OK"], ["financement_ok", "Financement OK"],
            ["assurance_justifiee", "Assurance justifiée"], ["liasse_signee", "Liasse signée"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 text-[12px]">
              <input type="checkbox" checked={!!conds[k]} disabled={!canEdit}
                onChange={(e) => onPatch({ conditions: { ...conds, [k]: e.target.checked } })}
                data-testid={`liv-cond-${k}`} />
              {l}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
